import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/db';

export function useProfileGuard() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bootedOut, setBootedOut] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const forceLogout = async () => {
      try {
        await supabase.auth.signOut();
      } catch {
        // ignore
      }
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch {
        // ignore
      }
      if (!cancelled) {
        setBootedOut(true);
        navigate('/login', { replace: true });
      }
    };

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        const {
          data: { user: currentUser },
          error: authError,
        } = await supabase.auth.getUser();

        if (cancelled) return;

        if (authError) {
          setError(authError.message || 'Failed to check session');
          await forceLogout();
          return;
        }

        if (!currentUser?.id) {
          setUser(null);
          setProfile(null);
          setLoading(false);
          navigate('/login', { replace: true });
          return;
        }

        setUser(currentUser);

        const { data: profileRow, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', currentUser.id)
          .maybeSingle();

        if (cancelled) return;

        if (profileError) {
          setError(profileError.message || 'Failed to load profile');
          setProfile(null);
          await forceLogout();
          return;
        }

        if (!profileRow) {
          setProfile(null);
          setError('Profile record not found for this account.');
          await forceLogout();
          return;
        }

        setProfile(profileRow);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Unexpected error loading profile');
        setProfile(null);
        await forceLogout();
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return { user, profile, loading, error, bootedOut };
}